import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Select, Input } from 'antd';
import { PoweroffOutlined, SearchOutlined } from '@ant-design/icons';
import queryString from 'query-string';
import { useQuery } from '@apollo/client';
import { userInfoVar } from 'src/apollo/cache';
import moment from 'moment';
import _ from 'lodash';
import { isMobileOnly } from 'react-device-detect';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import LeftMenu2 from 'src/components/LevelUp/LeftMenu/index2';
import LevelUpLibraryMenuGroup3 from 'src/components/common/LevelUpLibraryMenuGroup3';
import { EXTERNAL_USER_STUDY_LEVELUP } from 'src/operations/queries/externalStudy';
import { LEVEL_UP_POINT } from 'src/operations/queries/book';
import { GET_STUDENT_LUPUSER, GET_TEACHER_USER } from 'src/operations/queries/getUser';
import { getYearMonthArray } from 'src/utils';

const months = getYearMonthArray('2023-07', 0);
const { Option } = Select;

const RowFlex = styled.div`
  display: flex;
  flex-direction: row;
`;

const Wrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  height: 100vh;
  padding-top: ${({ $isMobile }) => ($isMobile ? '0' : '64px')};
  overflow: ${({ $isMobile }) => ($isMobile ? 'auto' : 'hidden')};
  box-sizing: border-box;
  background:
    radial-gradient(ellipse 800px 500px at 15% 30%, rgba(34, 211, 238, 0.08) 0%, transparent 70%),
    radial-gradient(ellipse 600px 400px at 85% 70%, rgba(16, 185, 129, 0.06) 0%, transparent 70%),
    radial-gradient(ellipse 500px 300px at 50% 10%, rgba(139, 92, 246, 0.04) 0%, transparent 70%),
    #e8f4f8;
`;

const MobileHeader = styled(RowFlex)`
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 12px 16px 14px;
  background: linear-gradient(135deg, #0c1a2e 0%, #1e3a5f 60%, #0891b2 100%);
  position: sticky;
  top: 0;
  z-index: 30;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
    background-size: 18px 18px;
    pointer-events: none;
  }
`;

const MobileHeaderBrand = styled(RowFlex)`
  position: relative;
  z-index: 1;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const MobileHeaderLogo = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 14px;
  background: linear-gradient(135deg, #0891b2, #06b6d4);
  background-image: url('/images/LevelUp/levelup_owl.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: 24px 24px;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.4);
  flex-shrink: 0;
`;

const MobileHeaderName = styled.div`
  font-family: 'Baloo 2', sans-serif;
  font-size: 18px;
  font-weight: 900;
  color: #ffffff;
  line-height: 1;
  span {
    color: #22d3ee;
  }
`;

const MobileHeaderActions = styled(RowFlex)`
  position: relative;
  z-index: 1;
  gap: 8px;
  align-items: center;
`;

const MobileIconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const MainWrapper = styled.div`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  min-height: ${({ $isMobile }) => ($isMobile ? 'auto' : 'calc(100vh - 64px)')};
  height: ${({ $isMobile }) => ($isMobile ? 'auto' : 'calc(100vh - 64px)')};
  width: 100%;
  overflow: ${({ $isMobile }) => ($isMobile ? 'visible' : 'hidden')};
`;

const Main = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: ${({ $isMobile }) => ($isMobile ? 'auto' : '100%')};
  padding: ${({ $isMobile }) => ($isMobile ? '0 12px 16px' : '12px 24px 20px')};
  display: flex;
  flex-direction: column;
  gap: ${({ $isMobile }) => ($isMobile ? '12px' : '16px')};
  overflow: ${({ $isMobile }) => ($isMobile ? 'visible' : 'hidden')};
  box-sizing: border-box;
`;

const MobileTopTabsShell = styled.div`
  position: fixed;
  top: 62px;
  left: 0;
  right: 0;
  z-index: 18;
  background: #ffffff;
  padding: 0 12px 10px;
  box-shadow: 0 8px 20px rgba(8, 145, 178, 0.08);
`;

const MobileTopTabs = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  padding: 10px 2px 0;
`;

const MobileTopTab = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 56px;
  padding: 4px;
  border-radius: 16px;
  border: 1.5px solid ${(props) => (props.$active ? 'rgba(8,145,178,.22)' : 'transparent')};
  background: ${(props) => (props.$active ? '#eaf7fb' : 'transparent')};
  color: ${(props) => (props.$active ? '#0891b2' : '#4a6380')};
  font-size: 9px;
  font-weight: 800;
  cursor: pointer;
  line-height: 1.1;
  font-family: 'Nunito', sans-serif;
`;

const Panel = styled.div`
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px 14px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.78);
  border: 1.5px solid rgba(8, 145, 178, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);
  overflow: hidden;
  @media (max-width: 768px) {
    flex: none;
    min-height: auto;
    padding: 12px 0 0;
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    overflow: visible;
  }
`;

const PanelTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
  @media (max-width: 768px) {
    display: none;
  }
`;

const PanelCount = styled.div`
  font-family: 'Baloo 2', 'Nunito', sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #172554;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CountBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(8, 145, 178, 0.12);
  color: #0891b2;
  border: 1px solid rgba(8, 145, 178, 0.18);
  font-family: 'JetBrains Mono', monospace;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchInput = styled(Input)`
  width: 150px;
  height: 36px;
  border-radius: 18px;
  border: 1.5px solid rgba(8, 145, 178, 0.14);
  background: #ffffff;
  font-family: 'Nunito', sans-serif;
  &:hover,
  &.ant-input-affix-wrapper-focused {
    border-color: rgba(8, 145, 178, 0.22);
    box-shadow: none;
  }
`;

const MonthSelect = styled(Select)`
  min-width: 136px;
  height: 36px;
  .ant-select-selector {
    height: 36px !important;
    border-radius: 18px !important;
    border: 1.5px solid rgba(8, 145, 178, 0.14) !important;
    display: flex;
    align-items: center;
    box-shadow: none !important;
  }
`;

const ScrollArea = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(8, 145, 178, 0.06);
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(8, 145, 178, 0.62), rgba(6, 182, 212, 0.52));
    border-radius: 999px;
  }
  @media (max-width: 768px) {
    flex: none;
    min-height: auto;
    overflow: visible;
    padding-right: 0;
  }
`;

const CardsWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding-bottom: 8px;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
  @media (max-width: 768px) {
    gap: 14px;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 24px;
  border: 1.5px solid rgba(8, 145, 178, 0.12);
  padding: 20px;
  box-shadow: 0 4px 18px rgba(8, 145, 178, 0.12);
  @media (max-width: 768px) {
    border-radius: 26px;
    padding: 18px;
  }
`;

const FullCard = styled(Card)`
  grid-column: 1 / -1;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #172554;
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 16px;
`;

const PointsHero = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 16px;
`;

const PointsOrb = styled.div`
  width: 82px;
  height: 82px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(135deg, #f97316, #ea580c);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 20px rgba(249, 115, 22, 0.3);
`;

const PointsOrbNum = styled.div`
  font-family: 'Baloo 2', sans-serif;
  font-size: 30px;
  font-weight: 900;
  color: #ffffff;
  line-height: 1;
`;

const PointsOrbLabel = styled.div`
  font-size: 8px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.75);
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const BarWrap = styled.div`
  flex: 1;
`;

const BarTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #172554;
  margin-bottom: 8px;
`;

const Track = styled.div`
  height: 9px;
  background: #dff3fb;
  border-radius: 5px;
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  width: ${(props) => `${props.$width}%`};
  background: linear-gradient(90deg, #f97316, #fbbf24);
  border-radius: 5px;
  transition: width 0.8s ease;
`;

const BarNote = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #8fa3bb;
  margin-top: 4px;
`;

const MissionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 14px;
`;

const MissionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 14px;
  background: #f4fbff;
  border: 1.5px solid rgba(8, 145, 178, 0.12);
`;

const MissionIcon = styled.span`
  font-size: 15px;
  flex-shrink: 0;
`;

const MissionLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  flex: 1;
`;

const MissionPts = styled.span`
  font-size: 10px;
  font-weight: 900;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  background: ${(props) => props.$bg || 'rgba(8,145,178,.12)'};
  color: ${(props) => props.$color || '#0891b2'};
`;

const MissionStatus = styled.span`
  font-size: 9px;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  background: ${(props) => props.$bg || 'rgba(139,92,246,.12)'};
  color: ${(props) => props.$color || '#8b5cf6'};
`;

const CalendarNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const CalendarTitle = styled.div`
  font-family: 'Baloo 2', sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: #172554;
`;

const CalendarNavBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 10px;
  border: 1.5px solid rgba(8, 145, 178, 0.14);
  background: #ffffff;
  color: #0891b2;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
`;

const CalendarHeader = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 700;
  text-align: center;
  color: #8fa3bb;
  padding: 4px 0;
  letter-spacing: 0.05em;
`;

const CalendarDay = styled.div`
  aspect-ratio: 1;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  background: ${(props) => {
    if (props.$empty) return '#f4fbff';
    if (props.$study) return '#0891b2';
    if (props.$today) return '#fff7ed';
    return '#eff8fb';
  }};
  color: ${(props) => {
    if (props.$empty) return '#64748b';
    if (props.$today && !props.$study) return '#f97316';
    if (props.$study) return '#ffffff';
    return '#64748b';
  }};
  border: 1.5px solid ${(props) => {
    if (props.$today) return '#f97316';
    if (props.$study) return 'rgba(8,145,178,.18)';
    return 'transparent';
  }};
`;

const MobileLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 12px;
  padding-left: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #4a6380;
`;

const MobileLegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const MobileLegendMark = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${(props) => (props.$outlined ? 'transparent' : props.$bg || '#0891b2')};
  border: 1.5px solid ${(props) => (props.$outlined ? props.$bg || '#f97316' : 'transparent')};
  box-sizing: border-box;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActivityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 16px;
  background: #f4fbff;
  border: 1.5px solid rgba(8, 145, 178, 0.12);
  transition: all 0.18s ease;
  &:hover {
    border-color: rgba(8, 145, 178, 0.24);
    transform: translateX(3px);
  }
`;

const ActivityIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  background: ${(props) => props.$bg || 'rgba(8,145,178,.1)'};
`;

const ActivityInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActivityTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #172554;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActivitySub = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #8fa3bb;
  margin-top: 1px;
`;

const ActivityRight = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const ActivityPts = styled.div`
  font-size: 13px;
  font-weight: 900;
  color: ${(props) => props.$color || '#0891b2'};
`;

const ActivityDate = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: #8fa3bb;
  margin-top: 1px;
`;

const EmptyBox = styled.div`
  flex: 1;
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  border: 1.5px dashed rgba(8, 145, 178, 0.18);
  color: #6b7280;
  background: rgba(255, 255, 255, 0.72);
  font-family: 'Baloo 2', 'Nunito', sans-serif;
  font-size: 24px;
  font-weight: 700;
`;

const MobileBottomNav = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  background: #ffffff;
  border-top: 1.5px solid rgba(8, 145, 178, 0.12);
  box-shadow: 0 -4px 20px rgba(8, 145, 178, 0.1);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  z-index: 40;
`;

const MobileBottomNavItem = styled.button`
  padding: 10px 4px 8px;
  border: 0;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  color: ${(props) => (props.$active ? '#0891b2' : '#8fa3bb')};
  font-size: 10px;
  font-weight: 700;
`;

const MobilePointsHeroCard = styled.div`
  border-radius: 26px;
  padding: 18px 18px 16px;
  background:
    radial-gradient(circle, rgba(255,255,255,.08) 1px, transparent 1px),
    linear-gradient(135deg, #ff7a18 0%, #ff5f00 100%);
  background-size: 16px 16px, auto;
  box-shadow: 0 14px 30px rgba(249, 115, 22, 0.28);
  color: #ffffff;
`;

const MobilePointsHeroTop = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

const MobilePointsBig = styled.div`
  min-width: 56px;
  text-align: left;
`;

const MobilePointsNumber = styled.div`
  font-family: 'Baloo 2', sans-serif;
  font-size: 34px;
  font-weight: 900;
  line-height: 1;
`;

const MobilePointsLabel = styled.div`
  margin-top: 3px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255,255,255,.82);
`;

const MobilePointsMeta = styled.div`
  flex: 1;
`;

const MobilePointsGoal = styled.div`
  font-size: 13px;
  font-weight: 900;
  margin-bottom: 10px;
`;

const MobilePointsTrack = styled.div`
  height: 10px;
  border-radius: 999px;
  background: rgba(255,255,255,.28);
  overflow: hidden;
`;

const MobilePointsFill = styled.div`
  height: 100%;
  width: ${(props) => `${props.$width}%`};
  background: #ffffff;
  border-radius: 999px;
`;

const MobilePointsNote = styled.div`
  margin-top: 8px;
  text-align: right;
  font-size: 11px;
  font-weight: 800;
  color: rgba(255,255,255,.9);
`;

function Calendar({ selectedMonth, uniqueStudyDates }) {
  const [year, month] = selectedMonth.split('-').map(Number);
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
  const totalDays = new Date(year, month, 0).getDate();
  const today = moment().format('YYYY-MM') === selectedMonth ? moment().date() : null;
  const daysOfWeek = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const cells = [];
  let day = 1;
  for (let row = 0; row < 6; row += 1) {
    for (let col = 0; col < 7; col += 1) {
      const index = col + row * 7;
      const dayOfMonth = index - firstDayOfMonth;
      if (dayOfMonth >= 0 && dayOfMonth < totalDays) {
        cells.push({
          day: dayOfMonth + 1,
          study: uniqueStudyDates.has(String(day).padStart(2, '0')),
          today: today === day,
        });
        day += 1;
      } else {
        cells.push({ empty: true });
      }
    }
  }

  while (cells.length > 35 && cells.slice(-7).every((cell) => cell.empty)) {
    cells.splice(-7, 7);
  }

  return (
    <>
      <CalendarGrid>
        {daysOfWeek.map((dayLabel) => (
          <CalendarHeader key={dayLabel}>{dayLabel}</CalendarHeader>
        ))}
        {cells.map((cell, index) => (
          <CalendarDay key={index} $empty={cell.empty} $study={cell.study} $today={cell.today}>
            {cell.empty ? '' : cell.day}
          </CalendarDay>
        ))}
      </CalendarGrid>
      {/* <Legend>
        <LegendItem>
          <LegendDot $bg="#0891b2" />
          <span>Study Day</span>
        </LegendItem>
        <LegendItem>
          <LegendDot $bg="#eff8fb" />
          <span>Normal Day</span>
        </LegendItem>
      </Legend> */}
    </>
  );
}

export default function LevelUpPointsAndCalendarPage() {
  const { student_idx, month } = queryString.parse(window.location.search);
  const history = useHistory();
  const isMobileView = window.isNativeAppMobile || isMobileOnly;
  const [selectedMonth, setSelectedMonth] = useState(months[0]);
  const [searchValue, setSearchValue] = useState('');
  const [currentLevel, setCurrentLevel] = useState(null);
  const user_idx = userInfoVar()?.idx || null;

  useEffect(() => {
    if (month) setSelectedMonth(month);
  }, [month]);

  const getLatestClassLevel = (classes = []) => {
    const latestClass = _.orderBy(
      classes.filter((classInfo) => classInfo?.levelup_level),
      [
        (classInfo) => {
          const beginDate = moment(classInfo?.begin_date);
          return beginDate.isValid() ? beginDate.valueOf() : 0;
        },
      ],
      ['desc'],
    )[0];
    return latestClass?.levelup_level || '';
  };

  let getUserType = GET_STUDENT_LUPUSER;
  if (userInfoVar() && userInfoVar()?.type !== '0' && userInfoVar()?.type !== 0) {
    getUserType = GET_TEACHER_USER;
  }

  useQuery(getUserType, {
    variables: {},
    onCompleted: (data) => {
      if (!data?.getUserData) return;
      let tempData = data.getUserData;
      if (tempData.class_teacher) {
        tempData = Object.assign({}, tempData, { classes: tempData.class_teacher.map((item) => item.class) });
      } else {
        tempData = Object.assign({}, tempData, { classes: tempData.class_student.map((item) => item.class) });
      }

      if (tempData?.user_levelup_tests?.length > 0) {
        const sortedTests = _.orderBy(
          tempData.user_levelup_tests.filter((item) => item.level),
          ['test_end_date'],
          ['desc'],
        );
        const latestTest = sortedTests[0];
        if (latestTest?.lexile && latestTest?.level) {
          setCurrentLevel(latestTest.level);
          return;
        }
      }

      setCurrentLevel(getLatestClassLevel(tempData?.classes));
    },
  });

  const { data } = useQuery(EXTERNAL_USER_STUDY_LEVELUP, {
    variables: {
      user_idx: student_idx ? parseInt(student_idx, 10) : parseInt(user_idx, 10),
      ym: selectedMonth,
      type: 'levelup',
      status: 2,
    },
    fetchPolicy: 'no-cache',
    skip: !user_idx,
  });

  const { data: pointdata } = useQuery(LEVEL_UP_POINT, {
    variables: {
      user_idx: student_idx ? parseInt(student_idx, 10) : parseInt(user_idx, 10),
      month: selectedMonth,
    },
    fetchPolicy: 'no-cache',
    skip: !user_idx,
  });

  const studyList = useMemo(() => data?.getExternalStuiesList || [], [data]);

  const formatStoredDate = (timestamp) => {
    if (!timestamp) return '-';

    const isoMatch = String(timestamp).match(/^(\d{4})-(\d{2})-(\d{2})T/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${year}-${month}-${day}`;
    }

    const parsed = moment(timestamp);
    if (!parsed.isValid()) return String(timestamp);
    return parsed.format('YYYY-MM-DD');
  };

  const getStoredMoment = (timestamp) => {
    if (!timestamp) return null;

    const isoMatch = String(timestamp).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (isoMatch) {
      const [, year, month, day, hour, minute, second] = isoMatch;
      return moment({
        year: parseInt(year, 10),
        month: parseInt(month, 10) - 1,
        date: parseInt(day, 10),
        hour: parseInt(hour, 10),
        minute: parseInt(minute, 10),
        second: parseInt(second, 10),
      });
    }

    const parsed = moment(timestamp);
    return parsed.isValid() ? parsed : null;
  };

  const levelupPoint = useMemo(() => {
    if (pointdata?.getLevelupPoints) {
      const points = pointdata.getLevelupPoints;
      const monthly = (points.month || []).reduce((acc, cur) => acc + parseInt(cur.points || 0, 10), 0);
      return { monthly, yearly: parseInt(points.year || 0, 10) };
    }
    return { monthly: 0, yearly: 0 };
  }, [pointdata]);

  const totalTime = useMemo(() => {
    let count = 0;
    if (studyList.length) {
      studyList.forEach((studyEle) => {
        const last = studyEle?.last_study_timestamp?.slice(0, 19);
        if (!last) return;
        const first = _.sortBy(
          (studyEle?.external_study_stage || [])
            .map((stage) => stage?.study_date?.slice(0, 19))
            .filter((value) => value && value.includes(last.slice(0, 10))),
          (value) => new moment(value),
        )[0];
        if (first) count += moment(last).diff(moment(first));
      });
    }
    return moment.utc(count).format('HH:mm:ss');
  }, [studyList]);

  const uniqueStudyDates = useMemo(() => new Set(studyList.map((item) => item.last_study_timestamp?.slice(8, 10)).filter(Boolean)), [studyList]);

  const monthActivities = useMemo(() => {
    const bookActivities = studyList.slice(0, 8).map((item) => {
      const articleData = item?.article_data ? JSON.parse(item.article_data) : {};
      const title = articleData?.title || 'Study';
      const level = articleData?.mx_level ? `mx ${String(articleData.mx_level).padStart(2, '0')}` : 'mx --';
      const lexile = articleData?.lexile_val ? `${articleData.lexile_val}L` : '-';
      const points = parseInt(item?.point || 3, 10) || 3;
      return {
        icon: '📘',
        bg: 'rgba(8,145,178,.1)',
        title: `${title} 학습`,
        sub: `${level} · ${lexile} · ${formatStoredDate(item?.last_study_timestamp)}`,
        pts: `　`,
        ptsColor: '#0891b2',
        date: getStoredMoment(item?.last_study_timestamp)?.fromNow() || '-',
      };
    });

    const pointActivities = (pointdata?.getLevelupPoints?.month || []).slice(0, 8).map((item) => ({
      icon: '🏅',
      bg: 'rgba(249,115,22,.1)',
      title: '포인트 적립',
      sub: formatStoredDate(item?.study_date),
      pts: `+${item?.points || 0}pt`,
      ptsColor: '#f97316',
      date: getStoredMoment(item?.study_date)?.fromNow() || '-',
    }));

    return [...bookActivities, ...pointActivities]
      .sort((a, b) => moment(b.sub.match(/\d{4}-\d{2}-\d{2}/)?.[0]).valueOf() - moment(a.sub.match(/\d{4}-\d{2}-\d{2}/)?.[0]).valueOf())
      .filter((item) => item.title.toLowerCase().includes(searchValue.toLowerCase()) || item.sub.toLowerCase().includes(searchValue.toLowerCase()))
      .slice(0, 12);
  }, [studyList, pointdata, searchValue]);

  const missions = useMemo(() => {
    const monthlyStudyCount = studyList.length;
    const attendanceCount = uniqueStudyDates.size;
    const fluencyCount = studyList.filter((item) => item?.recording_data !== null && item?.recording_data !== undefined).length;
    const bookReportCount = studyList.filter((item) => {
      if (!item?.data) return false;
      try {
        const reportData = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
        return reportData?.status === 2;
      } catch (error) {
        return false;
      }
    }).length;

    return [
      {
        icon: '📖',
        label: '읽기 완료',
        // pts: '+3pt',
        // ptsBg: 'rgba(8,145,178,.12)',
        ptsColor: '#0891b2',
        count: `${monthlyStudyCount}회`,
        countBg: 'rgba(8,145,178,.12)',
        countColor: '#0891b2',
      },
      {
        icon: '🗓️',
        label: '출석 체크',
        count: `${attendanceCount}회`,
        countBg: 'rgba(245,158,11,.12)',
        countColor: '#f59e0b',
      },
      {
        icon: '📝',
        label: '북리포트 작성',
        count: `${bookReportCount}회`,
        countBg: 'rgba(139,92,246,.12)',
        countColor: '#8b5cf6',
      },
      {
        icon: '🎙️',
        label: 'Fluency 녹음',
        count: `${fluencyCount}회`,
        countBg: 'rgba(249,115,22,.12)',
        countColor: '#f97316',
      },
    ];
  }, [studyList, uniqueStudyDates.size]);

  const monthlyGoal = 100;
  const monthlyPercent = Math.min(100, Math.round((levelupPoint.monthly / monthlyGoal) * 100));

  return (
    <Wrapper $isMobile={isMobileView}>
      {isMobileView ? (
        <>
          <MobileHeader>
            <MobileHeaderBrand>
              <MobileHeaderLogo />
              <MobileHeaderName>
                e-<span>Library</span>
              </MobileHeaderName>
            </MobileHeaderBrand>
            <MobileHeaderActions>
              <MobileIconButton type="button" onClick={() => history.push('/level-up/calendar')}>
                {'\u{1F514}'}
              </MobileIconButton>
              <MobileIconButton type="button" onClick={() => history.push('/')}>
                <PoweroffOutlined />
              </MobileIconButton>
            </MobileHeaderActions>
          </MobileHeader>
        </>
      ) : null}
      <MainWrapper $isMobile={isMobileView}>
        {!isMobileView && (
          <LeftMenu2
            myLevel={parseInt(userInfoVar()?.type || 0, 10) !== 0 ? -2 : currentLevel === 'PH' ? 0 : parseInt(currentLevel, 10)}
            isStudent={parseInt(userInfoVar()?.type || 0, 10) === 0}
          />
        )}
        <Main
          $isMobile={isMobileView}
          style={isMobileView ? { paddingTop: '86px', paddingBottom: 'calc(104px + env(safe-area-inset-bottom, 0px))' } : undefined}
        >
          {isMobileView && (
            <MobileTopTabsShell>
              <MobileTopTabs>
                <MobileTopTab type="button" onClick={() => history.push('/level-up/favorite')}>
                  <span>{'\u2764\uFE0F'}</span>
                  <span>Favorite</span>
                </MobileTopTab>
                <MobileTopTab type="button" onClick={() => history.push('/level-up/inprogress')}>
                  <span>{'\u{1F4D6}'}</span>
                  <span>In Progress</span>
                </MobileTopTab>
                <MobileTopTab type="button" onClick={() => history.push('/level-up/portfolio')}>
                  <span>{'\u2705'}</span>
                  <span>Portfolio</span>
                </MobileTopTab>
                <MobileTopTab $active type="button">
                  <span>{'\u{1F3C6}'}</span>
                  <span>Pts & Cal</span>
                </MobileTopTab>
              </MobileTopTabs>
            </MobileTopTabsShell>
          )}
          {!isMobileView && <LevelUpLibraryMenuGroup3 currentMenu="calendar" student_idx={student_idx} />}
          <Panel>
            <PanelTop>
              <PanelCount>
                <span>Pts & Calendar</span>
                <CountBadge>{selectedMonth}</CountBadge>
              </PanelCount>
              <Toolbar>
                <MonthSelect bordered={false} value={selectedMonth} onChange={setSelectedMonth}>
                  {months.map((item, key) => (
                    <Option key={`search-month-option${key}`} value={item}>
                      {item}
                    </Option>
                  ))}
                </MonthSelect>
                {/* <SearchInput
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  allowClear
                  bordered={false}
                  placeholder="Search"
                  suffix={<SearchOutlined style={{ color: '#64748b' }} />}
                /> */}
              </Toolbar>
            </PanelTop>
            <ScrollArea>
              {isMobileView ? (
                <CardsWrap>
                  <MobilePointsHeroCard>
                    <MobilePointsHeroTop>
                      <MobilePointsBig>
                        <MobilePointsNumber>{levelupPoint.monthly}</MobilePointsNumber>
                        <MobilePointsLabel>POINTS</MobilePointsLabel>
                      </MobilePointsBig>
                      <MobilePointsMeta>
                        <MobilePointsGoal>{`이달 목표: ${monthlyGoal}pt`}</MobilePointsGoal>
                        <MobilePointsTrack>
                          <MobilePointsFill $width={monthlyPercent} />
                        </MobilePointsTrack>
                        <MobilePointsNote>{`목표까지 ${Math.max(monthlyGoal - levelupPoint.monthly, 0)}pt 남았어요!`}</MobilePointsNote>
                      </MobilePointsMeta>
                    </MobilePointsHeroTop>
                  </MobilePointsHeroCard>

                  <Card>
                    <CalendarNav>
                      <CalendarNavBtn
                        type="button"
                        onClick={() => {
                          const currentIndex = months.indexOf(selectedMonth);
                          if (currentIndex < months.length - 1) setSelectedMonth(months[currentIndex + 1]);
                        }}
                      >
                        {'\u2039'}
                      </CalendarNavBtn>
                      <CalendarTitle>{moment(`${selectedMonth}-01`).format('MMMM YYYY')}</CalendarTitle>
                      <CalendarNavBtn
                        type="button"
                        onClick={() => {
                          const currentIndex = months.indexOf(selectedMonth);
                          if (currentIndex > 0) setSelectedMonth(months[currentIndex - 1]);
                        }}
                      >
                        {'\u203A'}
                      </CalendarNavBtn>
                    </CalendarNav>
                    <Calendar selectedMonth={selectedMonth} uniqueStudyDates={uniqueStudyDates} />
                    <MobileLegend>
                      <MobileLegendItem>
                        <MobileLegendMark $bg="#0891b2" />
                        <span>학습일</span>
                      </MobileLegendItem>
                      <MobileLegendItem>
                        <MobileLegendMark $bg="#f97316" $outlined />
                        <span>오늘</span>
                      </MobileLegendItem>
                    </MobileLegend>
                  </Card>

                  <FullCard>
                    <CardTitle>
                      <span>📚</span>
                      <span>최근 활동 내역</span>
                    </CardTitle>
                    {monthActivities.length ? (
                      <ActivityList>
                        {monthActivities.map((item, index) => (
                          <ActivityRow key={`${item.title}-${index}`}>
                            <ActivityIcon $bg={item.bg}>{item.icon}</ActivityIcon>
                            <ActivityInfo>
                              <ActivityTitle>{item.title}</ActivityTitle>
                              <ActivitySub>{item.sub}</ActivitySub>
                            </ActivityInfo>
                            <ActivityRight>
                              <ActivityPts $color={item.ptsColor}>{item.pts}</ActivityPts>
                              <ActivityDate>{item.date}</ActivityDate>
                            </ActivityRight>
                          </ActivityRow>
                        ))}
                      </ActivityList>
                    ) : (
                      <EmptyBox style={{ minHeight: 180 }}>No Activity</EmptyBox>
                    )}
                  </FullCard>
                </CardsWrap>
              ) : (
                <CardsWrap>
                <Card>
                  <CardTitle>
                    <span>🏆</span>
                    <span>Monthly Points</span>
                  </CardTitle>
                  <PointsHero>
                    <PointsOrb>
                      <PointsOrbNum>{levelupPoint.monthly}</PointsOrbNum>
                      <PointsOrbLabel>Points</PointsOrbLabel>
                    </PointsOrb>
                    <BarWrap>
                      <BarTitle>{`이달 목표 : ${monthlyGoal}pt`}</BarTitle>
                      <Track>
                        <Fill $width={monthlyPercent} />
                      </Track>
                      <BarNote>{`목표까지 ${Math.max(monthlyGoal - levelupPoint.monthly, 0)}pt 남았어요!`}</BarNote>
                    </BarWrap>
                  </PointsHero>
                  <MissionList>
                    {missions.map((mission) => (
                      <MissionRow key={mission.label}>
                        <MissionIcon>{mission.icon}</MissionIcon>
                        <MissionLabel>{mission.label}</MissionLabel>
                        {mission.pts ? (
                          <MissionPts $bg={mission.ptsBg} $color={mission.ptsColor}>
                            {mission.pts}
                          </MissionPts>
                        ) : null}
                        <MissionStatus $bg={mission.countBg} $color={mission.countColor}>
                          {mission.count}
                        </MissionStatus>
                      </MissionRow>
                    ))}
                  </MissionList>
                </Card>

                <Card>
                  <CardTitle>
                    <span>🗓️</span>
                    <span>학습 캘린더</span>
                  </CardTitle>
                  <CalendarNav>
                    <CalendarNavBtn type="button" onClick={() => {
                      const currentIndex = months.indexOf(selectedMonth);
                      if (currentIndex < months.length - 1) setSelectedMonth(months[currentIndex + 1]);
                    }}>
                      ‹
                    </CalendarNavBtn>
                    <CalendarTitle>{selectedMonth}</CalendarTitle>
                    <CalendarNavBtn type="button" onClick={() => {
                      const currentIndex = months.indexOf(selectedMonth);
                      if (currentIndex > 0) setSelectedMonth(months[currentIndex - 1]);
                    }}>
                      ›
                    </CalendarNavBtn>
                  </CalendarNav>
                  <Calendar selectedMonth={selectedMonth} uniqueStudyDates={uniqueStudyDates} />
                  <MobileLegend>
                    <MobileLegendItem>
                      <MobileLegendMark $bg="#0891b2" />
                      <span>학습일</span>
                    </MobileLegendItem>
                    <MobileLegendItem>
                      <MobileLegendMark $bg="#f97316" $outlined />
                      <span>오늘</span>
                    </MobileLegendItem>
                  </MobileLegend>
                </Card>

                <FullCard>
                  <CardTitle>
                    <span>📚</span>
                    <span>최근 활동 내역</span>
                  </CardTitle>
                  {monthActivities.length ? (
                    <ActivityList>
                      {monthActivities.map((item, index) => (
                        <ActivityRow key={`${item.title}-${index}`}>
                          <ActivityIcon $bg={item.bg}>{item.icon}</ActivityIcon>
                          <ActivityInfo>
                            <ActivityTitle>{item.title}</ActivityTitle>
                            <ActivitySub>{item.sub}</ActivitySub>
                          </ActivityInfo>
                          <ActivityRight>
                            <ActivityPts $color={item.ptsColor}>{item.pts}</ActivityPts>
                            <ActivityDate>{item.date}</ActivityDate>
                          </ActivityRight>
                        </ActivityRow>
                      ))}
                    </ActivityList>
                  ) : (
                    <EmptyBox style={{ minHeight: 180 }}>No Activity</EmptyBox>
                  )}
                </FullCard>
              </CardsWrap>
              )}
            </ScrollArea>
          </Panel>
          {isMobileView && (
            <MobileBottomNav>
              <MobileBottomNavItem type="button" onClick={() => history.push('/level-up?mobilePage=home')}>
                <span>{'\u{1F3E0}'}</span>
                <span>Home</span>
              </MobileBottomNavItem>
              <MobileBottomNavItem type="button" onClick={() => history.push('/level-up/library?lv=1')}>
                <span>{'\u{1F4DA}'}</span>
                <span>Library</span>
              </MobileBottomNavItem>
              <MobileBottomNavItem type="button" onClick={() => history.push('/level-up/portfolio')}>
                <span>{'\u{1F4C1}'}</span>
                <span>Portfolio</span>
              </MobileBottomNavItem>
              <MobileBottomNavItem type="button" onClick={() => history.push('/level-up?mobilePage=ranking')}>
                <span>{'\u{1F3C6}'}</span>
                <span>Ranking</span>
              </MobileBottomNavItem>
              <MobileBottomNavItem type="button" onClick={() => history.push('/level-up?mobilePage=profile')}>
                <span>{'\u{1F464}'}</span>
                <span>내 정보</span>
              </MobileBottomNavItem>
            </MobileBottomNav>
          )}
        </Main>
      </MainWrapper>
    </Wrapper>
  );
}
